import { toast } from '@/components/toast';
import { BULK_LIMIT } from '@/constants';
import { persistentClipsDns } from '@/environment';
import { parseIdbDraftIntoLibraryItem, useProjectIdb } from '@/pages/AdvancedEditor/hooks/polotno-store/store-init/useProjectIdb';
import { useSceneNotifications } from '@/pages/avatar';
import {
    deleteItemFromLibrary,
    deleteManyProjects,
    GetLibraryItemsResponse,
    getOrganization,
    getProjectLibraryItems,
} from '@/services/api';
import { useAuth } from '@/services/auth';
import { useFlag } from '@/services/featureFlags';
import { logger } from '@/services/logger';
import { LibraryItem, LibraryItemStatus, LibraryItemType } from '@d-id/types';
import { debounce } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from 'react-query';
import { useSearchParams } from 'react-router-dom';
import { atom, useRecoilState, useRecoilValue } from 'recoil';

export const GET_ITEM_KEY = 'GET_ITEM_KEY';
export const GET_PROJECTS_KEY = 'GET_PROJECTS_KEY';
export const CLIP_DRIVERS_KEY = 'CLIP_DRIVERS_KEY';

export type ORDER = 'ascending' | 'descending';

export type SORT_BY = 'created_at' | 'modified_at' | 'name';

export enum FilterBy {
    ALL = 'all',
    VIDEOS = 'videos',
    DRAFTS = 'drafts',
}
type VideoQueryProps = { isAnonymous?: boolean };

export type VideoFilters = {
    nameContains?: string;
};

export const videoFilters = atom<VideoFilters>({
    key: 'video-filters',
    default: {},
});

export const videoIdToPlay = atom<string | undefined>({
    key: 'autoplay-video-id',
    default: undefined,
});

export const selectedVideos = atom({
    key: 'selected-videos',
    default: new Map<string, LibraryItem>(),
});

export function useGetProjects({ isAnonymous }: VideoQueryProps) {
    const [searchParams] = useSearchParams();
    const includeExternalProjectsFlag = useFlag('shared-gallery-enabled', false);

    const { filter_by, sort_by, order, created_by } = useMemo(() => {
        return {
            filter_by: searchParams.get('filter_by') as FilterBy,
            sort_by: searchParams.get('sort_by') as SORT_BY,
            order: searchParams.get('order') as ORDER,
            created_by: searchParams.get('created_by') as string,
        };
    }, [searchParams]);

    const includeExternalProjects = useMemo(() => {
        // Only exclude external projects when created_by is explicitly empty string (all org videos selected)
        if (created_by === '') {
            return false;
        }

        return includeExternalProjectsFlag;
    }, [created_by, includeExternalProjectsFlag]);

    return useInfiniteQuery<GetLibraryItemsResponse>(
        [GET_PROJECTS_KEY, filter_by, sort_by, order, created_by, includeExternalProjects],
        ({ pageParam: token }) =>
            getProjectLibraryItems(!!isAnonymous, { token, filter_by, sort_by, order, created_by, includeExternalProjects }),
        {
            refetchOnWindowFocus: false,
            /**
             * Setting cache to 0 prevents React Query from caching every filter change
             * Caching that way would refetch a bunch when altering or invalidating GET_PROJECTS_KEY
             */
            cacheTime: 0,
            getNextPageParam: ({ token }) => token,
            onError: err => {
                logger.error('Could not fetch projects', { err });
                toast.error({ title: 'Could not fetch projects', toastId: `${GET_PROJECTS_KEY}-error` });
            },
        }
    );
}

export interface ProjectsQueryData {
    items?: LibraryItem[];
    pages?: { items: LibraryItem[] }[];
}

export function useCurrentLibraryItems() {
    const { isAnonymous } = useAuth();
    const { data: projects } = useGetProjects({ isAnonymous });
    const { idbDraft } = useProjectIdb();
    const fetchGuestDraftEnabled = useFlag('fetch_guest_draft_enabled', false);
    const draft = idbDraft.data && fetchGuestDraftEnabled ? parseIdbDraftIntoLibraryItem(idbDraft.data) : undefined;
    useSceneNotifications();

    const items = projects?.pages?.reduce((items: LibraryItem[], page: { items: LibraryItem[] }) => [...items, ...page.items], []) || [];
    return {
        items: draft ? [draft, ...items] : items,
        libraryLoaded: !!projects,
    };
}

export function useGetOrganization() {
    const { user } = useAuth();

    return useQuery('ORGANIZATION_NAME', () => getOrganization(user?.org_id ?? ''), {
        onError(err) {
            logger.error('Could not fetch organization', { err });
            toast.error({ title: 'Could not fetch organization', toastId: `fetch-org-error` });
        },
        enabled: !!user?.org_id,
    });
}

export function useDeleteItemFromLibraryMutation(skipToast?: boolean) {
    const { refetchVideos } = useRefetchVideos();

    return useMutation(deleteItemFromLibrary, {
        onError: async (err: any, { videoId, type }) => {
            await refetchVideos();
            if (err.response.status !== 404) {
                logger.error(`Could not delete video ${type} - ${videoId}`, { err });
                if (!skipToast) {
                    toast.error({ title: `Could not delete video`, toastId: `library-item-delete-error` });
                }
            }
        },
        onSuccess: async () => {
            await refetchVideos();
        },
    });
}

export function useBulkDeleteFromLibraryMutation(skipToast?: boolean) {
    const queryClient = useQueryClient();

    return useMutation(async (ids: string[]) => deleteManyProjects(ids), {
        onMutate: async (ids: string[]) => {
            await queryClient.cancelQueries({ queryKey: GET_PROJECTS_KEY });
            const previousData = queryClient.getQueryData<ProjectsQueryData>(GET_PROJECTS_KEY);
            const filterDeletedItems = (item: LibraryItem) => !ids.includes(item.id);
            queryClient.setQueryData<ProjectsQueryData>(GET_PROJECTS_KEY, old => {
                const items = old?.items?.filter(filterDeletedItems) || [];
                const pages = old?.pages?.map(page => ({ items: page.items.filter(filterDeletedItems) || [] })) || [];

                return {
                    items,
                    pages,
                };
            });

            return { previousData };
        },
        onError: async (err: any, ids, context) => {
            if (err?.response.status !== 404) {
                logger.error(`Could not delete projects - ${ids}`, { err });
                if (!skipToast) {
                    toast.error({ title: `Could not delete videos`, toastId: `library-project-bulk-delete-error` });
                }
            }
            queryClient.setQueryData(GET_PROJECTS_KEY, context?.previousData);
        },

        onSuccess: async () => {
            if (!skipToast) {
                toast.success({ title: 'Your videos were successfully deleted' });
            }
        },
        onSettled: () => {
            queryClient.invalidateQueries({ queryKey: GET_PROJECTS_KEY });
        },
    });
}

export function useRefetchVideos() {
    const queryClient = useQueryClient();

    return {
        refetchVideos: () => queryClient.refetchQueries(GET_PROJECTS_KEY),
    };
}

export function useFilterVideos() {
    const { isOrg } = useAuth();
    const filters = useRecoilValue(videoFilters);
    const [debouncedSearch, setDebouncedSearch] = useState(filters.nameContains);

    const updateDebouncedState = useMemo(
        () =>
            debounce((nameContains: string) => {
                setDebouncedSearch(nameContains);
            }, 500),
        [setDebouncedSearch]
    );

    useEffect(() => {
        updateDebouncedState(filters.nameContains || '');
    }, [filters.nameContains]);

    const getFilteredVideos = useCallback(
        (videos: LibraryItem[]) => {
            return videos.filter((item: LibraryItem) => {
                const nameIncluded = item.name?.toLocaleLowerCase().includes(debouncedSearch?.toLocaleLowerCase() || '');

                if (!nameIncluded) {
                    return false;
                }

                return true;
            });
        },
        [debouncedSearch, filters, isOrg]
    );

    return {
        getFilteredVideos,
    };
}

export function useSelectedVideos() {
    const [selected, setSelected] = useRecoilState(selectedVideos);

    const limitReached = selected.size >= BULK_LIMIT;

    const onSelect = (item: LibraryItem) => {
        setSelected(currSelected => {
            if (currSelected.has(item.id)) {
                currSelected.delete(item.id);
            } else if (!limitReached) {
                currSelected.set(item.id, item);
            }
            return new Map(currSelected);
        });
    };

    const selectMode = !!selected.size;
    const isSelected = (id: string) => selected.has(id);

    return {
        onSelect,
        selectMode,
        isSelected,
        selected,
        reset: () => setSelected(new Map()),
        bulkActionsEnabled: true,
        limitReached,
    };
}

export const defaultLibraryItem: LibraryItem = {
    id: '-1',
    thumbnail_url: encodeURI(`${persistentClipsDns}${'/welcome_video_thumbnail.png'}`),
    duration: 60,
    created_at: new Date().toISOString(),
    modified_at: new Date().toISOString(),
    status: LibraryItemStatus.Done,
    video_url: encodeURI(`${persistentClipsDns}${'/welcome_video.mp4'}`),
    name: 'Welcome to D-ID',
    type: LibraryItemType.CLIP,
    is_default: true,
};
